import {
  AddressDisplay,
  Badge,
  Button,
  cn,
  CopyButton,
  Glass,
  Input,
  Select,
  toast,
  Toggle,
  Tooltip,
  useCopyToClipboard,
} from "@gotts.ai/ui";
import {
  AlertTriangle,
  ChevronDown,
  Copy,
  Eye,
  EyeOff,
  FileDown,
  Key,
  PlugZap,
  Send,
  ShieldCheck,
  UserCheck,
  UserX,
  Wallet,
} from "lucide-react";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { encodeFunctionData, formatUnits } from "viem";

import { AccountIdenticon } from "../../components/AccountIdenticon";
import { AnimatedBalance } from "../../components/AnimatedBalance";
import { MaxButton } from "../../components/MaxButton";
import { PcbCard } from "../../components/PcbCard";
import { TokenIcon } from "../../components/TokenIcon";
import { useDeployment, useEvmBackend, useWallet } from "../../context";
import { useBlockNumber } from "../../hooks/useBlockNumber";
import { useRpcRequest } from "../../hooks/useRpcRequest";
import { MOCK_ERC20_ABI } from "../../lib/abis";
import {
  DEVENV_TOKEN_PRICES,
  getAgentForAccountIndex,
  TOKEN_DECIMALS,
} from "../../lib/constants";
import {
  formatEth as formatEthSmart,
  formatTokenBalance,
  formatUsd,
} from "../../lib/format";

import { DrainConfirmModal } from "./DrainConfirmModal";
import { FundAccountPanel } from "./FundAccountPanel";

import type { Account } from "./types";
import type { BadgeVariant } from "@gotts.ai/ui";
import type { Address } from "viem";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function ethFromHex(weiHex: string): number {
  return Number(BigInt(weiHex)) / 1e18;
}

const ANVIL_MNEMONIC =
  "test test test test test test test test test test test junk";

// ---------------------------------------------------------------------------
// Mnemonic & derivation path (Anvil standard)
// ---------------------------------------------------------------------------

function derivationPath(index: number): string {
  return `m/44'/60'/0'/0/${String(index)}`;
}

// Tier badge color map
const TIER_COLORS: Record<string, BadgeVariant | undefined> = {
  sovereign: undefined,
  trusted: "success",
  verified: "info",
  basic: undefined,
  unverified: "warning",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function KeyManagement() {
  const { request } = useRpcRequest();
  const { data: blockNumber } = useBlockNumber();
  const { copiedKey, copy } = useCopyToClipboard();
  const { address: connectedAddress, connectAnvil } = useWallet();
  const { accounts } = useEvmBackend();

  const [balances, setBalances] = useState<Record<string, string>>({});
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  // Drain state
  const [drainLoading, setDrainLoading] = useState<string | null>(null);
  const [drainModalAccount, setDrainModalAccount] = useState<Account | null>(
    null,
  );
  const [drainAllLoading, setDrainAllLoading] = useState(false);

  // Token balances per account (expanded)
  const { deployment } = useDeployment();
  const [tokenBalanceAccounts, setTokenBalanceAccounts] = useState<Set<string>>(
    new Set(),
  );
  const [accountTokenBalances, setAccountTokenBalances] = useState<
    Record<string, Record<string, string>>
  >({});
  const [tokenBalanceLoading, setTokenBalanceLoading] = useState<string | null>(
    null,
  );

  // Send ETH inline form
  const [sendEthAccount, setSendEthAccount] = useState<string | null>(null);
  const [sendEthRecipient, setSendEthRecipient] = useState("");
  const [sendEthAmount, setSendEthAmount] = useState("");
  const [sendEthLoading, setSendEthLoading] = useState(false);
  const [customSendRecipient, setCustomSendRecipient] = useState("");

  // Impersonation state
  const [impersonated, setImpersonated] = useState<Set<string>>(new Set());
  const [impersonateLoading, setImpersonateLoading] = useState<string | null>(
    null,
  );

  // View mode
  const [viewMode, setViewMode] = useState<"compact" | "cards">("compact");

  // New state
  const [showAllKeys, setShowAllKeys] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showMnemonic, setShowMnemonic] = useState(false);

  // Nonce state
  const [nonces, setNonces] = useState<Record<string, number>>({});

  // Export dropdown
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);

  // Role filter
  const [roleFilter, setRoleFilter] = useState("all");

  // Click-outside handler for export dropdown
  useEffect(() => {
    if (!exportOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) {
        setExportOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [exportOpen]);

  // -------------------------------------------------------------------------
  // Derived values
  // -------------------------------------------------------------------------

  const totalEth = useMemo(() => {
    return accounts.reduce((sum, acct) => {
      const hex = balances[acct.address];
      return sum + (hex ? ethFromHex(hex) : 0);
    }, 0);
  }, [accounts, balances]);

  const totalPortfolioUsd = useMemo(() => {
    let usd = totalEth * (DEVENV_TOKEN_PRICES.WETH ?? 0);
    // Add token balances across all accounts
    for (const acct of accounts) {
      const tokenBals = accountTokenBalances[acct.address];
      if (!tokenBals) continue;
      for (const [sym, valStr] of Object.entries(tokenBals)) {
        if (valStr === "0" || valStr === "--") continue;
        const num = parseFloat(valStr.replace(/,/g, ""));
        const price = DEVENV_TOKEN_PRICES[sym] ?? 0;
        if (!isNaN(num)) usd += num * price;
      }
    }
    return usd;
  }, [accounts, totalEth, accountTokenBalances]);

  const fundedAccountCount = useMemo(() => {
    return accounts.filter((acct) => {
      const hex = balances[acct.address];
      return hex && BigInt(hex) > 0n;
    }).length;
  }, [accounts, balances]);

  const filteredAccounts = useMemo(() => {
    let result = [...accounts];

    // Role filter
    if (roleFilter !== "all") {
      result = result.filter((acct) => {
        const idx = accounts.indexOf(acct);
        const agent = getAgentForAccountIndex(idx);
        switch (roleFilter) {
          case "deployer":
            return idx === 0;
          case "agents":
            return !!agent;
          case "funded": {
            const hex = balances[acct.address];
            return hex && BigInt(hex) > 0n;
          }
          case "unfunded": {
            const hex = balances[acct.address];
            return !hex || BigInt(hex) === 0n;
          }
          default:
            return true;
        }
      });
    }

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (acct) =>
          acct.label.toLowerCase().includes(q) ||
          acct.address.toLowerCase().includes(q),
      );
    }

    return result;
  }, [accounts, searchQuery, roleFilter, balances]);

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  const fetchBalances = useCallback(async () => {
    const results: Record<string, string> = {};
    for (const account of accounts) {
      try {
        const balance = (await request("eth_getBalance", [
          account.address,
          "latest",
        ])) as string;
        results[account.address] = balance;
      } catch {
        results[account.address] = "0x0";
      }
    }
    setBalances(results);
  }, [accounts, request]);

  const fetchNonces = useCallback(async () => {
    const results: Record<string, number> = {};
    for (const account of accounts) {
      try {
        const hex = (await request("eth_getTransactionCount", [
          account.address,
          "latest",
        ])) as string;
        results[account.address] = parseInt(hex, 16);
      } catch {
        results[account.address] = 0;
      }
    }
    setNonces(results);
  }, [accounts, request]);

  useEffect(() => {
    void fetchBalances();
    void fetchNonces();
  }, [fetchBalances, fetchNonces, blockNumber]);

  // Auto-fetch token balances for all visible accounts
  useEffect(() => {
    if (!deployment?.tokens) return;
    for (const acct of filteredAccounts) {
      if (!accountTokenBalances[acct.address]) {
        void fetchTokenBalancesFor(acct.address);
      }
    }
  }, [deployment?.tokens, filteredAccounts.length]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  function toggleKey(address: string) {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(address)) {
        next.delete(address);
      } else {
        next.add(address);
      }
      return next;
    });
  }

  async function fetchTokenBalancesFor(accountAddr: string) {
    if (!deployment?.tokens) return;
    setTokenBalanceLoading(accountAddr);
    const results: Record<string, string> = {};
    const tokens = deployment.tokens as unknown as Record<string, Address>;
    for (const [sym, tokenAddr] of Object.entries(tokens)) {
      try {
        const calldata = encodeFunctionData({
          abi: MOCK_ERC20_ABI,
          functionName: "balanceOf",
          args: [accountAddr as Address],
        });
        const raw = (await request("eth_call", [
          { to: tokenAddr, data: calldata },
          "latest",
        ])) as string;
        if (raw && raw !== "0x") {
          const bal = BigInt(raw);
          const decimals = TOKEN_DECIMALS[sym.toUpperCase()] ?? 18;
          const formatted = parseFloat(formatUnits(bal, decimals));
          results[sym.toUpperCase()] = formatTokenBalance(
            formatted,
            sym.toUpperCase(),
          );
        } else {
          results[sym.toUpperCase()] = "0";
        }
      } catch {
        results[sym.toUpperCase()] = "--";
      }
    }
    setAccountTokenBalances((prev) => ({ ...prev, [accountAddr]: results }));
    setTokenBalanceLoading(null);
  }

  function toggleTokenBalances(accountAddr: string) {
    setTokenBalanceAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(accountAddr)) {
        next.delete(accountAddr);
      } else {
        next.add(accountAddr);
        void fetchTokenBalancesFor(accountAddr);
      }
      return next;
    });
  }

  /** Resolve the actual recipient address from dropdown state. */
  function resolveRecipient(): string {
    return sendEthRecipient === "__other__"
      ? customSendRecipient
      : sendEthRecipient;
  }

  async function handleSendEth(fromAddr: string) {
    const recipient = resolveRecipient();
    if (!recipient || !sendEthAmount) return;
    setSendEthLoading(true);
    try {
      const weiAmount = BigInt(Math.floor(parseFloat(sendEthAmount) * 1e18));
      await request("anvil_impersonateAccount", [fromAddr]);
      await request("eth_sendTransaction", [
        {
          from: fromAddr,
          to: recipient,
          value: "0x" + weiAmount.toString(16),
          gas: "0x" + (21000).toString(16),
        },
      ]);
      await request("anvil_stopImpersonatingAccount", [fromAddr]);
      toast.success(
        `Sent ${sendEthAmount} ETH from ${fromAddr.slice(0, 6)}... to ${recipient.slice(0, 6)}...`,
      );
      setSendEthAccount(null);
      setSendEthRecipient("");
      setSendEthAmount("");
      setCustomSendRecipient("");
      await fetchBalances();
    } catch (err) {
      toast.error(
        `Send failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setSendEthLoading(false);
    }
  }

  function handleShowAllKeysChange(checked: boolean) {
    setShowAllKeys(checked);
    if (!checked) setExpandedKeys(new Set());
  }

  function isKeyVisible(address: string): boolean {
    return showAllKeys || expandedKeys.has(address);
  }

  /** Build recipient options excluding the sender. */
  function getRecipientOptions(senderAddress: string) {
    return [
      ...accounts
        .filter((a) => a.address !== senderAddress)
        .map((a, _i) => {
          const idx = accounts.indexOf(a);
          const agent = getAgentForAccountIndex(idx);
          const agentSuffix = agent ? ` \u00b7 ${agent.name}` : "";
          return {
            label: `${a.label}${agentSuffix} (${a.address.slice(0, 6)}...${a.address.slice(-4)})`,
            value: a.address,
          };
        }),
      { label: "Other", value: "__other__" },
    ];
  }

  /** Set max sendable amount (balance - 0.01 ETH for gas). */
  function handleMaxSend(accountAddress: string) {
    const hex = balances[accountAddress];
    if (!hex) return;
    const eth = ethFromHex(hex);
    const maxSend = Math.max(0, eth - 0.01);
    setSendEthAmount(maxSend.toString());
  }

  async function handleDrain(address: string) {
    const deployer = accounts[0]?.address;
    if (!deployer) return;
    setDrainLoading(address);
    setDrainModalAccount(null);
    try {
      const balanceHex = (await request("eth_getBalance", [
        address,
        "latest",
      ])) as string;
      const balance = BigInt(balanceHex);
      const gasPriceHex = (await request("eth_gasPrice")) as string;
      const gasPrice = BigInt(gasPriceHex);
      const gasCost = 21000n * gasPrice;
      if (balance <= gasCost) {
        toast.error("Insufficient balance to cover gas");
        return;
      }
      const sendAmount = balance - gasCost;

      await request("anvil_impersonateAccount", [address]);
      await request("eth_sendTransaction", [
        {
          from: address,
          to: deployer,
          value: "0x" + sendAmount.toString(16),
          gas: "0x" + (21000).toString(16),
        },
      ]);
      await request("anvil_stopImpersonatingAccount", [address]);
      await request("evm_mine");

      const drainedEth = (Number(sendAmount) / 1e18).toFixed(4);
      toast.success(
        `Drained ${drainedEth} ETH from ${address.slice(0, 6)}...${address.slice(-4)} to deployer`,
      );
      await fetchBalances();
      await fetchNonces();
    } catch (err) {
      toast.error(
        `Drain failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setDrainLoading(null);
    }
  }

  async function handleDrainAll() {
    const confirmed = window.confirm(
      "Drain all ETH from accounts 1-9 to the deployer account? This cannot be undone.",
    );
    if (!confirmed) return;
    setDrainAllLoading(true);
    let totalDrained = 0;
    let errors = 0;
    const deployer = accounts[0]?.address;
    if (!deployer) return;

    for (let i = 1; i < accounts.length; i++) {
      const address = accounts[i]?.address;
      if (!address) continue;
      try {
        const balanceHex = (await request("eth_getBalance", [
          address,
          "latest",
        ])) as string;
        const balance = BigInt(balanceHex);
        const gasPriceHex = (await request("eth_gasPrice")) as string;
        const gasPrice = BigInt(gasPriceHex);
        const gasCost = 21000n * gasPrice;
        if (balance <= gasCost) continue;
        const sendAmount = balance - gasCost;

        await request("anvil_impersonateAccount", [address]);
        await request("eth_sendTransaction", [
          {
            from: address,
            to: deployer,
            value: "0x" + sendAmount.toString(16),
            gas: "0x" + (21000).toString(16),
          },
        ]);
        await request("anvil_stopImpersonatingAccount", [address]);
        totalDrained += Number(sendAmount) / 1e18;
      } catch {
        errors++;
      }
    }

    // Ensure all mined blocks are committed
    await request("evm_mine");
    await fetchBalances();
    await fetchNonces();
    setDrainAllLoading(false);

    if (errors > 0) {
      toast.warning(
        `Drained ${totalDrained.toFixed(2)} ETH with ${String(errors)} error(s)`,
      );
    } else {
      toast.success(
        `Drained ${totalDrained.toFixed(2)} ETH from 9 accounts to deployer`,
      );
    }
  }

  function buildEnvString(): string {
    return accounts
      .map((a, i) => {
        const prefix =
          i === 0 ? "ANVIL_DEPLOYER" : `ANVIL_ACCOUNT_${String(i)}`;
        return `${prefix}_ADDRESS=${a.address}\n${prefix}_KEY=${a.key}`;
      })
      .join("\n");
  }

  function handleCopyEnv() {
    void navigator.clipboard.writeText(buildEnvString());
    toast.success("Copied .env format to clipboard");
  }

  function handleExportEnv() {
    const blob = new Blob([buildEnvString()], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "anvil-accounts.env";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Downloaded anvil-accounts.env");
  }

  async function handleImpersonate(address: string) {
    setImpersonateLoading(address);
    try {
      await request("anvil_impersonateAccount", [address]);
      setImpersonated((prev) => new Set(prev).add(address));
      toast.success(
        `Impersonating ${address.slice(0, 6)}...${address.slice(-4)}`,
      );
    } catch (err) {
      toast.error(
        `Impersonate failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setImpersonateLoading(null);
    }
  }

  async function handleStopImpersonating(address: string) {
    setImpersonateLoading(address);
    try {
      await request("anvil_stopImpersonatingAccount", [address]);
      setImpersonated((prev) => {
        const next = new Set(prev);
        next.delete(address);
        return next;
      });
      toast.success(
        `Stopped impersonating ${address.slice(0, 6)}...${address.slice(-4)}`,
      );
    } catch (err) {
      toast.error(
        `Stop impersonating failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setImpersonateLoading(null);
    }
  }

  const activeAccount = accounts.find((a) => a.address === connectedAddress);

  /** Compute per-account USD value (ETH + tokens). */
  function accountUsdValue(address: string): number {
    const hex = balances[address];
    const ethVal = hex ? ethFromHex(hex) * (DEVENV_TOKEN_PRICES.WETH ?? 0) : 0;
    let tokenVal = 0;
    const tokenBals = accountTokenBalances[address];
    if (tokenBals) {
      for (const [sym, valStr] of Object.entries(tokenBals)) {
        if (valStr === "0" || valStr === "--") continue;
        const num = parseFloat(valStr.replace(/,/g, ""));
        const price = DEVENV_TOKEN_PRICES[sym] ?? 0;
        if (!isNaN(num)) tokenVal += num * price;
      }
    }
    return ethVal + tokenVal;
  }

  // -------------------------------------------------------------------------
  // Inline Send ETH form (shared between table and cards views)
  // -------------------------------------------------------------------------

  function renderSendEthForm(
    account: (typeof accounts)[number],
    layout: "table" | "cards",
  ) {
    if (sendEthAccount !== account.address) return null;
    const balance = balances[account.address];
    const availableEth = balance ? ethFromHex(balance) : 0;
    const recipientOptions = getRecipientOptions(account.address);

    return (
      <div
        className={cn("glass-card space-y-2 p-2", layout === "cards" && "mb-3")}
      >
        <div className="flex items-center justify-between">
          <div className="font-sans text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
            Send ETH
          </div>
          <div className="text-text-muted font-mono text-[10px]">
            Available: {formatEthSmart(availableEth)} ETH
          </div>
        </div>
        <Select
          options={recipientOptions}
          value={sendEthRecipient}
          onValueChange={(v) => {
            setSendEthRecipient(v);
            if (v !== "__other__") setCustomSendRecipient("");
          }}
          placeholder="Select recipient"
        />
        {sendEthRecipient === "__other__" && (
          <Input
            type="text"
            placeholder="0x... custom recipient"
            value={customSendRecipient}
            onChange={(e) => {
              setCustomSendRecipient(e.target.value);
            }}
          />
        )}
        <div className="flex items-center gap-2">
          <Input
            type="number"
            placeholder="Amount"
            value={sendEthAmount}
            onChange={(e) => {
              setSendEthAmount(e.target.value);
            }}
            className="flex-1"
          />
          <span className="font-mono text-xs text-[var(--color-text-muted)]">
            ETH
          </span>
          <MaxButton
            onClick={() => {
              handleMaxSend(account.address);
            }}
          />
          <Button
            variant="gold"
            size="sm"
            onClick={() => void handleSendEth(account.address)}
            loading={sendEthLoading}
            disabled={sendEthLoading || !resolveRecipient() || !sendEthAmount}
          >
            Send
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSendEthAccount(null);
              setSendEthRecipient("");
              setSendEthAmount("");
              setCustomSendRecipient("");
            }}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div>
      <div className="space-y-6">
        {/* Summary Stats Row */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
          <PcbCard>
            <div className="hero-stat-label flex items-center gap-1.5">
              <TokenIcon symbol="ETH" size={16} />
              Total ETH
            </div>
            <AnimatedBalance
              value={totalEth}
              decimals={2}
              compact
              suffix=" ETH"
              className="stat-number text-[32px]"
              emDash={false}
            />
            <div className="text-text-dim mt-1 font-mono text-xs">
              {"\u2248"} {formatUsd(totalPortfolioUsd, { compact: true })}
            </div>
            <div className="text-text-dim mt-0.5 font-mono text-[10px]">
              across {accounts.length} accounts
            </div>
          </PcbCard>

          <PcbCard>
            <div className="hero-stat-label flex items-center gap-1.5">
              <Wallet size={14} />
              Accounts
            </div>
            <AnimatedBalance
              value={accounts.length}
              integer
              compact={false}
              className="stat-number text-[28px]"
              emDash={false}
            />
            <div className="text-text-dim mt-1 flex items-center gap-1 font-mono text-xs">
              {fundedAccountCount === accounts.length && (
                <span className="text-[var(--color-success)]">{"\u2713"}</span>
              )}
              {fundedAccountCount}/{accounts.length} funded
            </div>
          </PcbCard>

          <PcbCard>
            <div className="hero-stat-label flex items-center gap-1.5">
              <ShieldCheck size={14} />
              Active Account
            </div>
            {activeAccount ? (
              <>
                <div className="flex items-center gap-1.5 font-mono text-sm text-[var(--color-gold)]">
                  <span className="animate-pulse-green inline-block h-2 w-2 rounded-full bg-[var(--color-success)]" />
                  {activeAccount.address.slice(0, 10)}...
                  {activeAccount.address.slice(-8)}
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-text-dim font-mono text-xs">
                    {activeAccount.label}
                  </span>
                  <Badge variant="success">Connected</Badge>
                </div>
              </>
            ) : (
              <div className="text-text-dim mt-1 font-mono text-sm">None</div>
            )}
          </PcbCard>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Left: search + role filter + toggle */}
          <Input
            type="text"
            placeholder="Search accounts..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            className="w-full sm:w-48"
          />
          <Select
            options={[
              { label: "All", value: "all" },
              { label: "Deployer", value: "deployer" },
              { label: "Agents", value: "agents" },
              { label: "Funded", value: "funded" },
              { label: "Unfunded", value: "unfunded" },
            ]}
            value={roleFilter}
            onValueChange={setRoleFilter}
            className="w-28"
          />
          <div className="flex items-center gap-1.5">
            <Eye size={14} className="text-[var(--color-text-muted)]" />
            <Toggle
              checked={showAllKeys}
              onCheckedChange={handleShowAllKeysChange}
              label="Show Keys"
            />
          </div>
          <div className="flex-1" />
          {/* View mode toggle */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => {
                setViewMode("compact");
              }}
              className={cn(
                "btn-ghost",
                viewMode === "compact" &&
                  "bg-[rgba(201,168,76,0.12)] !text-[var(--color-gold)]",
              )}
            >
              Table
            </button>
            <button
              onClick={() => {
                setViewMode("cards");
              }}
              className={cn(
                "btn-ghost",
                viewMode === "cards" &&
                  "bg-[rgba(201,168,76,0.12)] !text-[var(--color-gold)]",
              )}
            >
              Cards
            </button>
          </div>
          {/* Export dropdown */}
          <div ref={exportRef} className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setExportOpen(!exportOpen);
              }}
            >
              Export
              <ChevronDown size={14} className="ml-1 inline-block" />
            </Button>
            {exportOpen && (
              <div className="glass-card absolute right-0 top-full z-20 mt-1 min-w-[160px] py-1">
                <button
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-[rgba(255,255,255,0.05)]"
                  onClick={() => {
                    handleCopyEnv();
                    setExportOpen(false);
                  }}
                >
                  <Copy size={14} />
                  Copy .env
                </button>
                <button
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-[rgba(255,255,255,0.05)]"
                  onClick={() => {
                    handleExportEnv();
                    setExportOpen(false);
                  }}
                >
                  <FileDown size={14} />
                  Download .env
                </button>
                <button
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-[rgba(255,255,255,0.05)]"
                  onClick={() => {
                    setShowMnemonic(!showMnemonic);
                    setExportOpen(false);
                  }}
                >
                  <Key size={14} />
                  Mnemonic
                </button>
              </div>
            )}
          </div>
          {/* Drain All */}
          <Tooltip content="Drain accounts 1-9 to deployer">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void handleDrainAll()}
              loading={drainAllLoading}
              disabled={drainAllLoading}
              className="btn-danger !bg-[rgba(199,75,80,0.15)]"
            >
              <AlertTriangle size={14} className="mr-1 inline-block" />
              Drain All to Deployer
            </Button>
          </Tooltip>
        </div>

        {/* Mnemonic Panel */}
        {showMnemonic && (
          <Glass className="p-4">
            <div className="mb-2 font-mono text-xs uppercase tracking-wider text-[var(--color-text-muted)]">
              HD Mnemonic
            </div>
            <div className="flex items-center gap-2 rounded bg-[rgba(0,0,0,0.3)] px-3 py-2">
              <code className="text-text-dim flex-1 font-mono text-sm">
                {ANVIL_MNEMONIC}
              </code>
              <CopyButton
                copied={copiedKey === "mnemonic"}
                onClick={() => copy(ANVIL_MNEMONIC, "mnemonic")}
                fontSize={11}
              />
            </div>
          </Glass>
        )}

        {/* Compact Table View */}
        {viewMode === "compact" && (
          <Glass className="overflow-x-auto p-0">
            <table className="w-full min-w-[800px] text-sm">
              <thead>
                <tr className="border-b border-[var(--glass-border)]">
                  <th className="py-2 pl-4 text-left font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
                    <Tooltip content="Account index">#</Tooltip>
                  </th>
                  <th className="py-2 text-left font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
                    Address
                  </th>
                  <th className="py-2 text-left font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
                    Label
                  </th>
                  <th className="w-28 whitespace-nowrap py-2 text-right font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
                    <Tooltip content="Native ETH balance">ETH</Tooltip>
                  </th>
                  <th className="w-24 whitespace-nowrap py-2 text-right font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
                    <Tooltip content="ETH + token balances in USD">
                      Value
                    </Tooltip>
                  </th>
                  <th className="w-16 whitespace-nowrap py-2 pr-4 text-right font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
                    <Tooltip content="Transaction count from this account">
                      <span className="cursor-help">Nonce</span>
                    </Tooltip>
                  </th>
                  <th className="min-w-[140px] max-w-[200px] py-2 pl-4 text-left font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
                    <Tooltip content="ERC-20 token balances from devenv seed">
                      Tokens
                    </Tooltip>
                  </th>
                  <th className="py-2 pr-4 text-right font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredAccounts.map((account) => {
                  const index = accounts.indexOf(account);
                  const isDeployer = index === 0;
                  const isActive = connectedAddress === account.address;
                  const balance = balances[account.address];
                  const tokenBals = accountTokenBalances[account.address];
                  const agent = getAgentForAccountIndex(index);
                  const keyVisible = isKeyVisible(account.address);
                  const nonce = nonces[account.address];

                  // Top 2 non-zero tokens for display (cap at 2 to prevent column overflow)
                  const nonZeroTokens = tokenBals
                    ? Object.entries(tokenBals).filter(
                        ([, v]) => v !== "0" && v !== "--",
                      )
                    : [];
                  const displayTokens = nonZeroTokens.slice(0, 2);
                  const overflowCount = Math.max(0, nonZeroTokens.length - 2);

                  const rowUsd = accountUsdValue(account.address);

                  return (
                    <Fragment key={account.address}>
                      <tr
                        className={cn(
                          "km-row-separator h-12 transition-colors hover:bg-[rgba(201,168,76,0.06)]",
                          isActive && "bg-[rgba(201,168,76,0.05)]",
                          isDeployer &&
                            "border-l-2 border-l-[var(--color-gold)]",
                          index % 2 === 1 && "bg-[rgba(255,255,255,0.015)]",
                        )}
                      >
                        <td className="py-2 pl-4">
                          <div className="flex items-center gap-1.5">
                            <AccountIdenticon
                              address={account.address}
                              size={20}
                            />
                            <div
                              className={cn(
                                "flex h-5 w-5 items-center justify-center rounded-full font-mono text-[10px] font-bold",
                                isDeployer
                                  ? "bg-[var(--color-gold)] text-[var(--color-bg)]"
                                  : "bg-[rgba(255,255,255,0.08)] text-[var(--color-text-dim)]",
                              )}
                            >
                              {isDeployer ? "\u2605" : index}
                            </div>
                          </div>
                        </td>
                        <td className="py-2">
                          <div className="flex flex-col gap-1">
                            <AddressDisplay
                              address={account.address}
                              leadChars={6}
                              tailChars={4}
                              fontSize={11}
                              copyable
                              copied={copiedKey === `addr-${account.address}`}
                              onCopy={() =>
                                copy(account.address, `addr-${account.address}`)
                              }
                            />
                            {keyVisible && (
                              <div className="flex items-center gap-1">
                                <code className="text-text-dim break-all font-mono text-[9px]">
                                  {account.key.slice(0, 18)}...
                                  {account.key.slice(-8)}
                                </code>
                                <CopyButton
                                  copied={
                                    copiedKey === `key-${account.address}`
                                  }
                                  onClick={() =>
                                    copy(account.key, `key-${account.address}`)
                                  }
                                  fontSize={8}
                                />
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-2">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-text-dim font-mono text-xs">
                              {account.label}
                            </span>
                            <div className="flex flex-wrap items-center gap-1.5">
                              {isDeployer && (
                                <Badge className="ml-0">Deployer</Badge>
                              )}
                              {isActive && (
                                <Badge variant="success" className="ml-0">
                                  Active
                                </Badge>
                              )}
                              {agent && (
                                <Badge
                                  variant={TIER_COLORS[agent.tier]}
                                  className="ml-0"
                                >
                                  {agent.name} (
                                  {agent.tier.charAt(0).toUpperCase() +
                                    agent.tier.slice(1)}
                                  )
                                </Badge>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap py-2 text-right font-mono text-xs text-[var(--color-success)]">
                          {balance ? (
                            <AnimatedBalance
                              value={ethFromHex(balance)}
                              decimals={2}
                              compact
                              emDash={false}
                              format={(v) =>
                                v < 0.001 ? "<0.001" : formatEthSmart(v)
                              }
                              className="text-[var(--color-success)]"
                            />
                          ) : (
                            "..."
                          )}
                        </td>
                        <td className="whitespace-nowrap py-2 text-right font-mono text-xs text-[var(--color-text-dim)]">
                          {formatUsd(rowUsd, { compact: true })}
                        </td>
                        <td className="whitespace-nowrap py-2 pr-4 text-right font-mono text-xs text-[var(--color-text-dim)]">
                          {nonce ?? "..."}
                        </td>
                        <td className="max-w-[200px] overflow-hidden py-2 pl-4">
                          {displayTokens.length > 0 ? (
                            <Tooltip
                              content={nonZeroTokens
                                .map(([sym, val]) => `${val} ${sym}`)
                                .join(", ")}
                            >
                              <div className="flex flex-wrap items-center gap-1.5">
                                {displayTokens.map(([sym, val]) => (
                                  <span
                                    key={sym}
                                    className="flex items-center gap-0.5 font-mono text-[10px] text-[var(--color-text-muted)]"
                                  >
                                    <TokenIcon symbol={sym} size={20} />
                                    <span>{val}</span>
                                    <span className="text-[var(--color-text-dim)]">
                                      {sym}
                                    </span>
                                  </span>
                                ))}
                                {overflowCount > 0 && (
                                  <span className="token-overflow-pill">
                                    +{overflowCount}
                                  </span>
                                )}
                              </div>
                            </Tooltip>
                          ) : (
                            <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
                              {"\u2014"}
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-4">
                          <div className="flex items-center justify-end gap-1">
                            {!isActive && (
                              <Tooltip content="Connect wallet">
                                <button
                                  onClick={() => {
                                    connectAnvil(
                                      account.address,
                                      account.label,
                                    );
                                  }}
                                  className="rounded p-1.5 text-[var(--color-text-muted)] hover:bg-[rgba(255,255,255,0.05)] hover:text-[var(--color-gold)]"
                                >
                                  <PlugZap size={16} />
                                </button>
                              </Tooltip>
                            )}
                            <Tooltip content="Send ETH">
                              <button
                                onClick={() => {
                                  setSendEthAccount(
                                    sendEthAccount === account.address
                                      ? null
                                      : account.address,
                                  );
                                }}
                                className="rounded p-1.5 text-[var(--color-text-muted)] hover:bg-[rgba(255,255,255,0.05)] hover:text-[#4a7fb5]"
                              >
                                <Send size={16} />
                              </button>
                            </Tooltip>
                            <Tooltip
                              content={
                                keyVisible
                                  ? "Hide private key"
                                  : "Show private key"
                              }
                            >
                              <button
                                onClick={() => {
                                  toggleKey(account.address);
                                }}
                                className="rounded p-1.5 text-[var(--color-text-muted)] hover:bg-[rgba(255,255,255,0.05)] hover:text-[var(--color-gold)]"
                              >
                                {keyVisible ? (
                                  <EyeOff size={16} />
                                ) : (
                                  <Eye size={16} />
                                )}
                              </button>
                            </Tooltip>
                            {impersonated.has(account.address) ? (
                              <Tooltip content="Stop impersonating">
                                <button
                                  onClick={() =>
                                    void handleStopImpersonating(
                                      account.address,
                                    )
                                  }
                                  disabled={impersonateLoading !== null}
                                  className="rounded p-1.5 text-[var(--color-warning)] hover:bg-[rgba(255,255,255,0.05)]"
                                >
                                  <UserX size={16} />
                                </button>
                              </Tooltip>
                            ) : (
                              <Tooltip content="Impersonate">
                                <button
                                  onClick={() =>
                                    void handleImpersonate(account.address)
                                  }
                                  disabled={impersonateLoading !== null}
                                  className="rounded p-1.5 text-[var(--color-text-muted)] hover:bg-[rgba(255,255,255,0.05)] hover:text-[var(--color-gold)]"
                                >
                                  <UserCheck size={16} />
                                </button>
                              </Tooltip>
                            )}
                          </div>
                        </td>
                      </tr>
                      {/* Inline Send ETH form row (table view) */}
                      {sendEthAccount === account.address && (
                        <tr>
                          <td colSpan={8} className="px-4 py-2">
                            {renderSendEthForm(account, "table")}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </Glass>
        )}

        {/* Account Cards Grid */}
        {viewMode === "cards" && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {filteredAccounts.map((account) => {
              const index = accounts.indexOf(account);
              const isDeployer = index === 0;
              const isActive = connectedAddress === account.address;
              const keyVisible = isKeyVisible(account.address);
              const balance = balances[account.address];
              const agent = getAgentForAccountIndex(index);
              const nonce = nonces[account.address];

              return (
                <Glass
                  key={account.address}
                  active={isActive}
                  className={cn(
                    "p-4",
                    isDeployer &&
                      "border border-[var(--color-gold)] border-opacity-30 bg-gradient-to-r from-[rgba(201,168,76,0.08)] to-transparent md:col-span-2",
                  )}
                >
                  {/* Card Header */}
                  <div className="mb-3 flex items-center gap-2">
                    <div
                      className={cn(
                        "flex h-7 w-7 items-center justify-center rounded-full font-mono text-xs font-bold",
                        isDeployer
                          ? "bg-[var(--color-gold)] text-[var(--color-bg)]"
                          : "bg-[rgba(255,255,255,0.08)] text-[var(--color-text-dim)]",
                      )}
                    >
                      {index}
                    </div>
                    <span className="text-text-dim font-mono text-sm">
                      {account.label}
                    </span>
                    {isDeployer && <Badge>Deployer</Badge>}
                    {isActive && <Badge variant="success">Active</Badge>}
                    {impersonated.has(account.address) && (
                      <Badge variant="warning">Impersonating</Badge>
                    )}
                    {agent && (
                      <Badge variant={TIER_COLORS[agent.tier]}>
                        {agent.name} (
                        {agent.tier.charAt(0).toUpperCase() +
                          agent.tier.slice(1)}
                        )
                      </Badge>
                    )}
                  </div>

                  {/* Address */}
                  <div className="mb-1">
                    <AddressDisplay
                      address={account.address}
                      copyable
                      copied={copiedKey === `addr-${account.address}`}
                      onCopy={() =>
                        copy(account.address, `addr-${account.address}`)
                      }
                      color="var(--color-gold)"
                      fontSize={12}
                    />
                  </div>
                  <p className="text-muted-foreground mb-2 font-mono text-[10px]">
                    {derivationPath(index)}
                    {nonce !== undefined && (
                      <span className="text-text-dim ml-2">nonce: {nonce}</span>
                    )}
                  </p>

                  {/* Balance */}
                  <div className="mb-1 flex items-baseline gap-1">
                    {balance ? (
                      <AnimatedBalance
                        value={ethFromHex(balance)}
                        decimals={2}
                        compact
                        emDash={false}
                        className="font-mono text-xl text-[var(--color-success)]"
                      />
                    ) : (
                      <span className="font-mono text-xl text-[var(--color-success)]">
                        ...
                      </span>
                    )}
                    <span className="text-text-muted font-mono text-sm">
                      ETH
                    </span>
                  </div>
                  {/* Inline token balance preview */}
                  {accountTokenBalances[account.address] && (
                    <div className="text-text-muted mb-3 flex flex-wrap items-center gap-x-2 gap-y-0.5 font-mono text-[10px]">
                      {Object.entries(
                        accountTokenBalances[account.address] as Record<
                          string,
                          string
                        >,
                      )
                        .filter(([, v]) => v !== "0" && v !== "--")
                        .map(([sym, val], i, arr) => (
                          <span key={sym} className="flex items-center gap-0.5">
                            <TokenIcon symbol={sym} size={16} />
                            {val} {sym}
                            {i < arr.length - 1 && (
                              <span className="text-text-dim ml-1">
                                {"\u00b7"}
                              </span>
                            )}
                          </span>
                        ))}
                    </div>
                  )}
                  {!accountTokenBalances[account.address] && (
                    <div className="mb-3" />
                  )}

                  {/* Private Key */}
                  <div className="mb-3">
                    <button
                      onClick={() => {
                        toggleKey(account.address);
                      }}
                      className="text-text-muted hover:text-text-dim mb-1 flex items-center gap-1 font-mono text-xs"
                    >
                      <span
                        className={cn(
                          "inline-block transition-transform",
                          keyVisible && "rotate-90",
                        )}
                      >
                        {"\u25b8"}
                      </span>
                      Private Key
                    </button>
                    {keyVisible && (
                      <div className="flex items-center gap-2 rounded bg-[rgba(0,0,0,0.3)] px-2 py-1.5">
                        <code className="text-text-dim flex-1 break-all font-mono text-[10px]">
                          {account.key}
                        </code>
                        <CopyButton
                          copied={copiedKey === `key-${account.address}`}
                          onClick={() =>
                            copy(account.key, `key-${account.address}`)
                          }
                          fontSize={9}
                        />
                      </div>
                    )}
                  </div>

                  {/* Token Balances (expandable) */}
                  <div className="mb-3">
                    <button
                      onClick={() => {
                        toggleTokenBalances(account.address);
                      }}
                      className="text-text-muted hover:text-text-dim mb-1 flex items-center gap-1 font-mono text-xs"
                    >
                      <span
                        className={cn(
                          "inline-block transition-transform",
                          tokenBalanceAccounts.has(account.address) &&
                            "rotate-90",
                        )}
                      >
                        {"\u25b8"}
                      </span>
                      Token Balances
                    </button>
                    {tokenBalanceAccounts.has(account.address) && (
                      <div className="ml-4 space-y-0.5">
                        {tokenBalanceLoading === account.address ? (
                          <span className="text-text-muted text-[10px]">
                            Loading...
                          </span>
                        ) : accountTokenBalances[account.address] ? (
                          Object.entries(
                            accountTokenBalances[account.address] as Record<
                              string,
                              string
                            >,
                          ).map(([sym, bal]) => (
                            <div
                              key={sym}
                              className="text-text-dim flex items-center justify-between font-mono text-[10px]"
                            >
                              <span className="text-text-muted flex items-center gap-1">
                                <TokenIcon symbol={sym} size={16} />
                                {sym}
                              </span>
                              <span
                                className={
                                  bal !== "0" && bal !== "--"
                                    ? "text-[var(--color-success)]"
                                    : ""
                                }
                              >
                                {bal}
                              </span>
                            </div>
                          ))
                        ) : null}
                      </div>
                    )}
                  </div>

                  {/* Send ETH Inline Form */}
                  {renderSendEthForm(account, "cards")}

                  {/* Actions Footer */}
                  <div className="flex items-center gap-2 border-t border-[var(--glass-border)] pt-3">
                    {isActive ? (
                      <Badge variant="success">Connected</Badge>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          connectAnvil(account.address, account.label);
                        }}
                      >
                        Connect
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSendEthAccount(
                          sendEthAccount === account.address
                            ? null
                            : account.address,
                        );
                      }}
                    >
                      Send ETH
                    </Button>
                    {impersonated.has(account.address) ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          void handleStopImpersonating(account.address)
                        }
                        loading={impersonateLoading === account.address}
                        disabled={impersonateLoading !== null}
                        className="text-[var(--color-warning)]"
                      >
                        Stop Impersonating
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void handleImpersonate(account.address)}
                        loading={impersonateLoading === account.address}
                        disabled={impersonateLoading !== null}
                      >
                        Impersonate
                      </Button>
                    )}
                    {!isDeployer && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setDrainModalAccount(account);
                        }}
                        disabled={drainLoading !== null || drainAllLoading}
                        className="text-[var(--color-error)]"
                      >
                        Drain to Deployer
                      </Button>
                    )}
                  </div>
                </Glass>
              );
            })}
          </div>
        )}

        <FundAccountPanel onFunded={fetchBalances} balances={balances} />

        <DrainConfirmModal
          account={drainModalAccount}
          onClose={() => {
            setDrainModalAccount(null);
          }}
          onConfirm={handleDrain}
          balances={balances}
          drainLoading={drainLoading}
        />
      </div>
    </div>
  );
}
